import React, { useState, useEffect, useRef } from "react";
import { Tree, TreeNode } from "react-organizational-chart";
import { getEmployeeList, fetchReportingDetails } from "../../../api/ApiConfig";
import { GoPeople } from "react-icons/go";
import { PiTreeStructureLight } from "react-icons/pi";
import people_find from "../../../images/people_find.png";
import search from "../../../images/search.png";
import "./PeopleTree.css";

// Reusable Employee Card Component with enhanced animations
const EmployeeCard = ({
  employee,
  isSelected = false,
  isSearched = false,
  onClick,
  animationDelay = 0,
  cardRef = null, // Added cardRef prop
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const internalCardRef = useRef(null);

  // Use provided ref or internal ref
  const actualCardRef = cardRef || internalCardRef;

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, animationDelay);

    return () => clearTimeout(timer);
  }, [animationDelay]);

  const getInitials = (name) => {
    if (!name) return "";
    const parts = name.trim().split(" ");
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(
      0
    )}`.toUpperCase();
  };

  const handleClick = () => {
    if (actualCardRef.current) {
      actualCardRef.current.style.transform = "scale(0.95)";
      setTimeout(() => {
        if (actualCardRef.current) {
          actualCardRef.current.style.transform = "scale(1)";
        }
      }, 150);
    }

    setTimeout(() => {
      onClick && onClick(employee);
    }, 200);
  };

  return (
    <div
      ref={actualCardRef}
      className={`employee-card ${isSelected ? "selected" : ""} ${
        isSearched ? "searched" : ""
      } ${isHovered ? "hovered" : ""} ${isVisible ? "card-visible" : ""}`}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-employee-id={employee?.employee_id} // Add data attribute for easy searching
    >
      <div className="employee-card-inner">
        <div className="avatar-container">
          {employee?.profile_photo ? (
            <img
              src={employee.profile_photo}
              alt={employee.name}
              className="avatar-image"
              onError={(e) => {
                e.target.style.display = "none";
                const initialsAvatar = e.target.nextElementSibling;
                if (initialsAvatar) {
                  initialsAvatar.style.display = "flex";
                }
              }}
            />
          ) : null}
          <div
            className="initials-avatar"
            style={{ display: employee?.profile_photo ? "none" : "flex" }}
          >
            {getInitials(employee?.name)}
          </div>
          {isSearched && <div className="searched-indicator"></div>}
        </div>

        <div className="info-container">
          <h4 className="employee-name">{employee?.name}</h4>
          <p className="employee-position">{employee?.designation}</p>
          <p className="employee-department">{employee?.department}</p>
          <p className="employee-id">ID: {employee?.employee_id}</p>
        </div>
       
      </div>
      <div className="card-shimmer"></div>
    </div>
  );
};

// Grid Layout Component for Multiple Reportees with staggered animations
const ReporteesGrid = ({ reportees, onNodeClick, selectedId, searchedId }) => {
  if (!reportees || reportees.length === 0) return null;

  return (
    <div className="reportees-grid-container">
      {reportees.map((report, index) => (
        <EmployeeCard
          key={report.employee_id}
          employee={report}
          isSelected={report.employee_id === selectedId}
          isSearched={report.employee_id === searchedId}
          onClick={onNodeClick}
          animationDelay={index * 50}
        />
      ))}
    </div>
  );
};

// Recursive Rendering Component for Direct Reportees
const OrgChartNode = ({
  employee,
  onNodeClick,
  selectedId,
  searchedId,
  level = 0,
  cardRefs, // Added cardRefs prop
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const nodeRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, level * 100);

    return () => clearTimeout(timer);
  }, [level]);

  if (!employee) return null;

  const hasManyReportees =
    employee.direct_reportees && employee.direct_reportees.length > 4;

  return (
    <TreeNode
      label={
        <div
          ref={nodeRef}
          className={`node-container ${isVisible ? "visible" : ""}`}
        >
          <EmployeeCard
            employee={employee}
            isSelected={employee.employee_id === selectedId}
            isSearched={employee.employee_id === searchedId}
            onClick={onNodeClick}
            animationDelay={level * 100}
            cardRef={cardRefs?.[employee.employee_id]} // Pass specific ref for this employee
          />
        </div>
      }
    >
      {hasManyReportees ? (
        <TreeNode
          label={
            <ReporteesGrid
              reportees={employee.direct_reportees}
              onNodeClick={onNodeClick}
              selectedId={selectedId}
              searchedId={searchedId}
            />
          }
        />
      ) : (
        employee.direct_reportees?.map((report) => (
          <OrgChartNode
            key={report.employee_id}
            employee={report}
            onNodeClick={onNodeClick}
            selectedId={selectedId}
            searchedId={searchedId}
            level={level + 1}
            cardRefs={cardRefs}
          />
        ))
      )}
    </TreeNode>
  );
};

// Typeahead Dropdown Component
const TypeaheadDropdown = ({
  results,
  onSelect,
  searchTerm,
  isVisible,
  onClose,
  selectedEmployee,
}) => {
  if (!isVisible) return null;

  return (
    <div className="typeahead-dropdown show">
      <div className="dropdown-header">
        <span className="dropdown-title">
          {searchTerm
            ? `Found ${results.length} result${results.length !== 1 ? "s" : ""}`
            : "Select an employee"}
        </span>
        <button className="dropdown-close" onClick={onClose}>
          ×
        </button>
      </div>

      {results.length === 0 ? (
        <div className="no-results">
          <div className="no-results-icon">
            <img src={search} alt="Search" />
          </div>
          <p>No employees found{searchTerm && ` for "${searchTerm}"`}</p>
        </div>
      ) : (
        <div className="typeahead-results">
          {results.map((employee, index) => (
            <div
              key={employee.Employee_ID}
              className={`typeahead-item ${
                selectedEmployee?.Employee_ID === employee.Employee_ID
                  ? "selected"
                  : ""
              }`}
              onClick={() => onSelect(employee)}
              style={{ animationDelay: `${index * 30}ms` }}
            >
              <div className="employee-avatar-small">
                {employee.profile_photo ? (
                  <img
                    src={employee.profile_photo}
                    alt={employee.Employee_Name}
                    className="avatar-image-small"
                    onError={(e) => {
                      e.target.style.display = "none";
                      const initialsAvatar = e.target.nextElementSibling;
                      if (initialsAvatar) {
                        initialsAvatar.style.display = "flex";
                      }
                    }}
                  />
                ) : null}
                <div
                  className="initials-avatar-small"
                  style={{ display: employee?.profile_photo ? "none" : "flex" }}
                >
                  {employee.Employee_Name?.split(" ")
                    .map((n) => n[0])
                    .join("")
                    .toUpperCase()
                    .slice(0, 2)}
                </div>
              </div>
              <div className="employee-info">
                <h4 className="typeahead-name">{employee.Employee_Name}</h4>
                <p className="typeahead-details">
                  {employee.Employee_Designation} - <span className="typeahead-department">
                  {employee.Name_of_Department}
                </span>
                </p>
                
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const PeopleFinder = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [orgStructure, setOrgStructure] = useState(null);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [searchedEmployeeId, setSearchedEmployeeId] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [fetchingDetails, setFetchingDetails] = useState(false);
  const [chartAnimation, setChartAnimation] = useState(false);

  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const chartRef = useRef(null);
  
  // New refs for scrolling to searched employee
  const employeeCardRefs = useRef({});
  const searchedEmployeeRef = useRef(null);

  // Fetch employees from API
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        setLoading(true);
        const employeeList = await getEmployeeList();
        setEmployees(employeeList);
      } catch (error) {
        console.error("Error fetching employees:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchEmployees();
  }, []);

  // Typeahead search
  useEffect(() => {
    if (!showDropdown) return;

    const searchLower = searchTerm.toLowerCase().trim();

    let results = employees;

    if (searchTerm.trim() !== "") {
      results = employees.filter((emp) => {
        const employeeName = (emp.Employee_Name || "").toLowerCase();
        const employeeId = (emp.Employee_ID || "").toLowerCase();
        const designation = (emp.designation || "").toLowerCase();
        const department = (emp.department || "").toLowerCase();

        return (
          employeeName.includes(searchLower) ||
          employeeId.includes(searchLower) ||
          designation.includes(searchLower) ||
          department.includes(searchLower)
        );
      });
    }

    results = results.sort((a, b) =>
      (a.Employee_Name || "").localeCompare(b.Employee_Name || "")
    );

    setSearchResults(results);
  }, [searchTerm, employees, showDropdown]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Scroll to searched employee when org structure is loaded
  useEffect(() => {
    if (searchedEmployeeId && orgStructure && chartAnimation) {
      scrollToSearchedEmployee();
    }
  }, [searchedEmployeeId, orgStructure, chartAnimation]);

  // Function to scroll to searched employee
  const scrollToSearchedEmployee = () => {
    setTimeout(() => {
      // Method 1: Find by data attribute
      const searchedCard = document.querySelector(
        `[data-employee-id="${searchedEmployeeId}"]`
      );
      
      // Method 2: Use refs
      const searchedCardRef = employeeCardRefs.current[searchedEmployeeId];
      
      const targetElement = searchedCard || searchedCardRef?.current;
      
      if (targetElement) {
        // Add highlight animation
        targetElement.classList.add('highlight-pulse');
        
        // Scroll to the element
        targetElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'center'
        });

        // Remove highlight after animation
        setTimeout(() => {
          targetElement.classList.remove('highlight-pulse');
        }, 2000);
      } else {
        console.log('Searched employee card not found in DOM yet');
        
        // Fallback: Try again after a short delay
        setTimeout(() => {
          const retryElement = document.querySelector(
            `[data-employee-id="${searchedEmployeeId}"]`
          );
          if (retryElement) {
            retryElement.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
              inline: 'center'
            });
          }
        }, 500);
      }
    }, 300);
  };

  // Build organization structure from API response
  const buildOrgStructure = (employeeData) => {
    if (!employeeData?.data || employeeData.data.length === 0) return null;

    const data = employeeData.data[0];
    const reportsToChain = data.reports_to || [];
    const selectedEmployee = data.employee;
    const directReportees = data.direct_reportees || [];

    const employeeWithReports = {
      ...selectedEmployee,
      direct_reportees: directReportees,
    };

    let rootEmployee = null;

    if (reportsToChain.length > 0) {
      const chain = [...reportsToChain].reverse();
      rootEmployee = { ...chain[0] };
      let currentLevel = rootEmployee;

      for (let i = 1; i < chain.length; i++) {
        const nextEmployee = { ...chain[i] };
        currentLevel.direct_reportees = [nextEmployee];
        currentLevel = nextEmployee;
      }

      if (currentLevel.direct_reportees) {
        if (
          !currentLevel.direct_reportees.find(
            (e) => e.employee_id === employeeWithReports.employee_id
          )
        ) {
          currentLevel.direct_reportees.push(employeeWithReports);
        } else {
          currentLevel.direct_reportees = currentLevel.direct_reportees.map(
            (e) =>
              e.employee_id === employeeWithReports.employee_id
                ? employeeWithReports
                : e
          );
        }
      } else {
        currentLevel.direct_reportees = [employeeWithReports];
      }
    } else {
      rootEmployee = employeeWithReports;
    }

    return rootEmployee;
  };

  // Fetch reporting details for selected employee
  const fetchEmployeeReportingDetails = async (empId, isSearch = false) => {
    try {
      setFetchingDetails(true);
      setChartAnimation(false);

      const response = await fetchReportingDetails({ emp_id: empId });

      if (response?.data && response.data.length > 0) {
        const builtStructure = buildOrgStructure(response);

        if (builtStructure) {
          setOrgStructure(builtStructure);
          setSelectedEmployeeId(empId);
          if (isSearch) {
            setSearchedEmployeeId(empId);
          }

          setTimeout(() => {
            setChartAnimation(true);
          }, 100);
        } else {
          setOrgStructure(null);
          console.warn(
            "Could not build org structure from API response.",
            response
          );
        }
      } else {
        setOrgStructure(null);
      }
    } catch (error) {
      console.error("Error fetching reporting details:", error);
      setOrgStructure(null);
    } finally {
      setFetchingDetails(false);
    }
  };

  // Function to get or create ref for an employee card
  const getEmployeeCardRef = (employeeId) => {
    if (!employeeCardRefs.current[employeeId]) {
      employeeCardRefs.current[employeeId] = React.createRef();
    }
    return employeeCardRefs.current[employeeId];
  };

  const handleNodeClick = (employee) => {
    if (employee?.employee_id && employee.employee_id !== selectedEmployeeId) {
      fetchEmployeeReportingDetails(employee.employee_id, false);
    }
  };

  const handleTypeaheadSelect = (employee) => {
    setSearchTerm(employee.Employee_Name);
    setShowDropdown(false);

    if (employee.Employee_ID) {
      fetchEmployeeReportingDetails(employee.Employee_ID, true);
    }
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    if (!showDropdown) {
      setShowDropdown(true);
    }
  };

  const handleInputFocus = () => {
    setShowDropdown(true);
  };

  const handleInputClick = () => {
    setShowDropdown(true);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      setShowDropdown(false);
      inputRef.current?.blur();
    } else if (e.key === "Enter" && searchResults.length > 0) {
      handleTypeaheadSelect(searchResults[0]);
    }
  };

  const handleDropdownClose = () => {
    setShowDropdown(false);
    inputRef.current?.focus();
  };

  const toggleDropdown = () => {
    setShowDropdown(!showDropdown);
    if (!showDropdown) {
      inputRef.current?.focus();
    }
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setOrgStructure(null);
    setSelectedEmployeeId(null);
    setSearchedEmployeeId(null);
    setChartAnimation(false);
    inputRef.current?.focus();
  };

  // Function to manually trigger scroll to searched employee
  const handleScrollToSearched = () => {
    if (searchedEmployeeId) {
      scrollToSearchedEmployee();
    }
  };

  return (
    <div className="people-finder-container">
      <header className="people-finder-header">
        {/* Subtle Animated Bubbles */}
        <div className="bubble-container">
          <div className="bubble"></div>
          <div className="bubble"></div>
          <div className="bubble"></div>
          <div className="bubble"></div>
          <div className="bubble"></div>
          <div className="bubble"></div>
          <div className="bubble"></div>
          <div className="bubble"></div>
        </div>
        
        <div>
          <span className="title-icon">
            <img
              src={people_find}
              alt="People Finder"
              className="title-image"
              style={{ width: 150 }}
            />
          </span>
        </div>
        <div className="people-finder-title">Welcome to People Finder</div>
        <div className="people-finder-subtitle">
          <span className="employee-departmen">
            Find anyone in your company instantly, see who they report to, and
            get their details. Stop searching in silos and start connecting with
            the right people to get work done faster.
          </span>
        </div>
      </header>

      <section className="search-section">
        <div className="dropdown-container">
          <div className="dropdown-input-container">
            <input
              ref={inputRef}
              type="text"
              placeholder="Search employee by name"
              value={searchTerm}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              onClick={handleInputClick}
              onKeyDown={handleKeyDown}
              className="dropdown-input"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={handleClearSearch}
                className="clear-button"
                title="Clear search"
              >
                ×
              </button>
            )}
            <button
              type="button"
              onClick={toggleDropdown}
              className="dropdown-toggle"
            >
              {showDropdown ? "▲" : "▼"}
            </button>
          </div>

          <div ref={dropdownRef} className="dropdown-wrapper">
            <TypeaheadDropdown
              results={searchResults}
              onSelect={handleTypeaheadSelect}
              searchTerm={searchTerm}
              isVisible={showDropdown}
              onClose={handleDropdownClose}
              selectedEmployee={employees.find(
                (e) => e.Employee_ID === selectedEmployeeId
              )}
            />
          </div>
        </div>

      
      </section>

      <section>
        {fetchingDetails ? (
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading employee hierarchy...</p>
          </div>
        ) : orgStructure ? (
          <div
            ref={chartRef}
            className={`chart-container ${chartAnimation ? "animated" : ""}`}
          >
            <div className="chart-header">
              <h3 className="chart-title">Employee Hierarchy</h3>
              <div className="chart-legend">
                <div className="legend-item">
                  <div className="legend-color searched"></div>
                  <span>Searched Employee</span>
                </div>
                <div className="legend-item">
                  <div className="legend-color selected"></div>
                  <span>Currently Selected</span>
                </div>
              </div>
            </div>
            <div className="chart-section">
              <div className="org-chart-center-wrapper">
                <Tree
                  lineWidth={"2px"}
                  lineColor={"#764ba2"}
                  lineBorderRadius={"10px"}
                  lineStyle="dashed"
                  label={
                    <EmployeeCard
                      employee={orgStructure}
                      isSelected={
                        orgStructure.employee_id === selectedEmployeeId
                      }
                      isSearched={
                        orgStructure.employee_id === searchedEmployeeId
                      }
                      onClick={handleNodeClick}
                      cardRef={getEmployeeCardRef(orgStructure.employee_id)}
                    />
                  }
                >
                  {orgStructure.direct_reportees?.length > 4 ? (
                    <TreeNode
                      label={
                        <ReporteesGrid
                          reportees={orgStructure.direct_reportees}
                          onNodeClick={handleNodeClick}
                          selectedId={selectedEmployeeId}
                          searchedId={searchedEmployeeId}
                        />
                      }
                    />
                  ) : (
                    orgStructure.direct_reportees?.map((report) => (
                      <OrgChartNode
                        key={report.employee_id}
                        employee={report}
                        onNodeClick={handleNodeClick}
                        selectedId={selectedEmployeeId}
                        searchedId={searchedEmployeeId}
                        level={1}
                        cardRefs={employeeCardRefs.current}
                      />
                    ))
                  )}
                </Tree>
              </div>
            </div>
          </div>
        ) : null}
      </section>
    </div>
  );
};

export default PeopleFinder;